/**
 * 자재 단가 DB (템플릿) — 건설/인테리어 자재별 표준 단가 카탈로그.
 *
 * 실제로는 서버 DB에서 불러오지만, 지금은 검색·추가 UX를 위한 샘플 데이터입니다.
 * 추후 API 연동 시 이 배열을 fetch 결과로 교체하면 됩니다.
 */
export interface MaterialDbEntry {
  id: string;
  name: string; // 자재명
  unit: string; // 단위
  unitPrice: number; // 표준 단가
  category: string; // 분류 (검색/필터용)
}

export const MATERIAL_DB: MaterialDbEntry[] = [
  {
    id: "db-1",
    name: "알루미늄창호",
    unit: "개",
    unitPrice: 120000,
    category: "창호",
  },
  {
    id: "db-2",
    name: "방충망",
    unit: "개",
    unitPrice: 15000,
    category: "창호",
  },
  {
    id: "db-3",
    name: "시스템창호",
    unit: "개",
    unitPrice: 250000,
    category: "창호",
  },
  {
    id: "db-4",
    name: "도배지(합지)",
    unit: "m²",
    unitPrice: 5000,
    category: "내장",
  },
  {
    id: "db-5",
    name: "도배지(실크)",
    unit: "m²",
    unitPrice: 9000,
    category: "내장",
  },
  {
    id: "db-6",
    name: "강마루",
    unit: "m²",
    unitPrice: 18000,
    category: "바닥",
  },
  {
    id: "db-7",
    name: "강화마루",
    unit: "m²",
    unitPrice: 22000,
    category: "바닥",
  },
  {
    id: "db-8",
    name: "타일(300각)",
    unit: "m²",
    unitPrice: 16000,
    category: "바닥",
  },
  {
    id: "db-9",
    name: "스프링클러 헤드",
    unit: "개",
    unitPrice: 50000,
    category: "소방",
  },
  {
    id: "db-10",
    name: "배관 자재(STS)",
    unit: "m",
    unitPrice: 12000,
    category: "소방",
  },
  {
    id: "db-11",
    name: "비상구유도등",
    unit: "개",
    unitPrice: 60000,
    category: "전기",
  },
  {
    id: "db-12",
    name: "비상조명등",
    unit: "개",
    unitPrice: 40000,
    category: "전기",
  },
  {
    id: "db-13",
    name: "석고보드(9.5T)",
    unit: "장",
    unitPrice: 4500,
    category: "내장",
  },
  {
    id: "db-14",
    name: "단열재(50T)",
    unit: "m²",
    unitPrice: 8500,
    category: "단열",
  },
  {
    id: "db-15",
    name: "페인트(수성)",
    unit: "L",
    unitPrice: 7000,
    category: "도장",
  },
];

/**
 * 시공/인건 단가 DB — 시공팀(공종)별 표준 인건 단가.
 * 직접노무비 패널의 "DB 검색 추가"에서 사용. 추후 서버 DB로 교체.
 */
export const LABOR_DB: MaterialDbEntry[] = [
  {
    id: "lb-1",
    name: "목공 시공 (인건)",
    unit: "평",
    unitPrice: 80000,
    category: "목공",
  },
  {
    id: "lb-2",
    name: "타일 시공 (인건)",
    unit: "m²",
    unitPrice: 35000,
    category: "타일",
  },
  {
    id: "lb-3",
    name: "도배 시공 (인건)",
    unit: "m²",
    unitPrice: 4000,
    category: "도배",
  },
  {
    id: "lb-4",
    name: "페인트 시공 (인건)",
    unit: "m²",
    unitPrice: 7000,
    category: "페인트",
  },
  {
    id: "lb-5",
    name: "철거 (인건)",
    unit: "m²",
    unitPrice: 15000,
    category: "철거",
  },
  {
    id: "lb-6",
    name: "전기 배선 (인건)",
    unit: "개소",
    unitPrice: 25000,
    category: "전기",
  },
  {
    id: "lb-7",
    name: "설비 배관 (인건)",
    unit: "m",
    unitPrice: 18000,
    category: "설비",
  },
  {
    id: "lb-8",
    name: "창호 설치 (인건)",
    unit: "개",
    unitPrice: 45000,
    category: "창호",
  },
  {
    id: "lb-9",
    name: "바닥 시공 (인건)",
    unit: "m²",
    unitPrice: 6000,
    category: "바닥",
  },
  {
    id: "lb-10",
    name: "미장 (인건)",
    unit: "m²",
    unitPrice: 12000,
    category: "미장",
  },
  {
    id: "lb-11",
    name: "방수 시공 (인건)",
    unit: "m²",
    unitPrice: 20000,
    category: "방수",
  },
  {
    id: "lb-12",
    name: "조명 설치 (인건)",
    unit: "개",
    unitPrice: 15000,
    category: "전기",
  },
  // ── 시간당(시급) 프리셋 — 단위 "시간" ──
  {
    id: "lb-h1",
    name: "목공 (시급)",
    unit: "시간",
    unitPrice: 30000,
    category: "목공",
  },
  {
    id: "lb-h2",
    name: "타일공 (시급)",
    unit: "시간",
    unitPrice: 35000,
    category: "타일",
  },
  {
    id: "lb-h3",
    name: "도배공 (시급)",
    unit: "시간",
    unitPrice: 28000,
    category: "도배",
  },
  {
    id: "lb-h4",
    name: "페인트공 (시급)",
    unit: "시간",
    unitPrice: 28000,
    category: "페인트",
  },
  {
    id: "lb-h5",
    name: "보통 인부 (시급)",
    unit: "시간",
    unitPrice: 20000,
    category: "공통",
  },
];
